import React, { useCallback, useState } from "react";
import axios from "axios";
import { TablePagination } from "@material-ui/core";
import { Link } from "react-router-dom";

import TablePaginationActions from "../../component/table/TablePagination";

const MANILA_OFFSET_MS = 8 * 60 * 60 * 1000;
const toDateInput = (date) => new Date(date.getTime() + MANILA_OFFSET_MS).toISOString().slice(0, 10);
const initialTo = new Date();
const initialFrom = new Date(initialTo.getTime() - 6 * 24 * 60 * 60 * 1000);
const userLabel = (user) => user?.username || user?.name || user?.uniqueId || "Unknown user";
const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—";

const UserGiftPairAnalysis = () => {
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState(toDateInput(initialFrom));
  const [to, setTo] = useState(toDateInput(initialTo));
  const [candidates, setCandidates] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadAnalysis = useCallback(async (user, page = 1, limit = pagination.limit) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get("suspiciousGiftPairs/analysis", {
        params: { userId: user._id, from, to, page, limit },
      });
      setSelectedUser(response.data.selectedUser);
      setPairs(response.data.pairs || []);
      setPagination(response.data.pagination || { page, limit, total: 0 });
      setCandidates([]);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Unable to load gift pairs.");
      setPairs([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, pagination.limit]);

  const submitSearch = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setSelectedUser(null);
    setPairs([]);
    try {
      const response = await axios.get("suspiciousGiftPairs/analysis/users", { params: { search } });
      const users = response.data.users || [];
      if (users.length === 1) {
        await loadAnalysis(users[0], 1, pagination.limit);
      } else {
        setCandidates(users);
        if (!users.length) setError("No matching users found.");
      }
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Unable to search users.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 className="mb-2 text-white">User Gift-Pair Analysis</h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/admin/dashboard" className="text-danger">Dashboard</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Gift-Pair Analysis</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header pb-0">
          <form className="row g-3 align-items-end my-2" onSubmit={submitSearch}>
            <div className="col-12 col-lg-5">
              <label htmlFor="analysis-user-search" className="form-label">User</label>
              <input id="analysis-user-search" className="form-control" required minLength="2" value={search}
                onChange={(event) => { setSearch(event.target.value); setSelectedUser(null); setCandidates([]); }}
                placeholder="Name, username, email, or unique ID" />
            </div>
            <div className="col-6 col-lg-2">
              <label htmlFor="analysis-from" className="form-label">From</label>
              <input id="analysis-from" type="date" className="form-control" required value={from} onChange={(event) => setFrom(event.target.value)} />
            </div>
            <div className="col-6 col-lg-2">
              <label htmlFor="analysis-to" className="form-label">To</label>
              <input id="analysis-to" type="date" className="form-control" required value={to} onChange={(event) => setTo(event.target.value)} />
            </div>
            <div className="col-12 col-lg-3">
              <button type="submit" className="btn btn-danger w-100" disabled={loading}>{loading ? "Searching…" : "Search"}</button>
            </div>
          </form>

          {candidates.length > 1 && (
            <div className="alert alert-dark mt-3" role="region" aria-label="Matching users">
              <p className="mb-2">Select the user to analyze:</p>
              <div className="d-flex flex-wrap gap-2">
                {candidates.map((user) => (
                  <button key={user._id} type="button" className="btn btn-sm btn-outline-danger" onClick={() => loadAnalysis(user, 1, pagination.limit)}>
                    {userLabel(user)}{user.uniqueId ? ` · ${user.uniqueId}` : ""}{user.email ? ` · ${user.email}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card-body card-overflow">
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          {selectedUser && <div className="mb-3"><strong>Selected user:</strong> {userLabel(selectedUser)}{selectedUser.uniqueId ? ` (${selectedUser.uniqueId})` : ""}</div>}
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead className="text-center"><tr>
                <th>User A → B</th><th>User B → A</th><th>Combined</th><th>Dominant direction</th>
                <th>Share</th><th>Transactions</th><th>Time range</th>
              </tr></thead>
              <tbody className="text-center">
                {loading ? <tr><td colSpan="7" className="py-5"><span className="spinner-border spinner-border-sm text-danger me-2" role="status" aria-hidden="true"></span>Loading analysis…</td></tr>
                  : !selectedUser ? <tr><td colSpan="7" className="py-5">Search for a user and date range to view gift pairs.</td></tr>
                    : pairs.length === 0 ? <tr><td colSpan="7" className="py-5">No gift pairs found for this user and date range.</td></tr>
                      : pairs.map((pair) => <tr key={`${pair.userA._id}-${pair.userB._id}`}>
                        <td><strong>{userLabel(pair.userA)}</strong><br /><span className="text-muted">{formatNumber(pair.userAToUserBDiamonds)} diamonds</span></td>
                        <td><strong>{userLabel(pair.userB)}</strong><br /><span className="text-muted">{formatNumber(pair.userBToUserADiamonds)} diamonds</span></td>
                        <td>{formatNumber(pair.combinedDiamonds)}</td>
                        <td>{userLabel(pair.dominantSender)} → {userLabel(pair.dominantReceiver)}</td>
                        <td><strong>{(Number(pair.dominantShare) * 100).toFixed(1)}%</strong></td>
                        <td>{formatNumber(pair.transactionCount)}</td>
                        <td><small>{formatDateTime(pair.firstTransactionAt)}<br />to {formatDateTime(pair.lastTransactionAt)}</small></td>
                      </tr>)}
              </tbody>
            </table>
          </div>
          {selectedUser && <TablePagination component="div" count={pagination.total} page={pagination.page - 1}
            rowsPerPage={pagination.limit} rowsPerPageOptions={[10, 20, 50, 100]}
            onChangePage={(_event, page) => loadAnalysis(selectedUser, page + 1, pagination.limit)}
            onChangeRowsPerPage={(event) => loadAnalysis(selectedUser, 1, Number(event.target.value))}
            ActionsComponent={TablePaginationActions} />}
        </div>
      </div>
    </>
  );
};

export default UserGiftPairAnalysis;
